import React, { useEffect, useRef, useState } from 'react';

const HeritageBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Detect mobile on mount and on resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Intersection observer for desktop scroll animations
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Shared inner content (stats, heading, ornaments etc.)
  const content = (isVisible: boolean) => (
    <>
      <div className={`hb-ornament ${isVisible ? 'hb--visible' : ''}`}>
        <div className="hb-ornament-line" />
        <div className="hb-ornament-diamond" />
        <div className="hb-ornament-line" />
      </div>

      <p className={`hb-eyebrow ${isVisible ? 'hb--visible' : ''}`}>
         Kryptonian Labs
      </p>

      <h2 className={`hb-heading ${isVisible ? 'hb--visible' : ''}`}>
        Our Heritage,<br />Refined in Gold.
      </h2>

      <div className={`hb-divider ${isVisible ? 'hb--visible' : ''}`} />

      <p className={`hb-body ${isVisible ? 'hb--visible' : ''}`}>
        Rooted in generations of craftsmanship, Kryptonian Labs brings timeless jewellery to life.
        Each piece is meticulously designed, blending tradition with modern elegance —
        created to celebrate moments that last forever.
      </p>

      <div className={`hb-stats ${isVisible ? 'hb--visible' : ''}`}>
        <div className="hb-stat">
          <span className="hb-stat-num">12+</span>
          <span className="hb-stat-label">Years of craft</span>
        </div>
        <div className="hb-stat-sep" />
        <div className="hb-stat">
          <span className="hb-stat-num">500+</span>
          <span className="hb-stat-label">Curated pieces</span>
        </div>
        <div className="hb-stat-sep" />
        <div className="hb-stat">
          <span className="hb-stat-num">100%</span>
          <span className="hb-stat-label">Authentic style</span>
        </div>
      </div>

      <div className={`hb-ornament hb-ornament-bottom ${isVisible ? 'hb--visible' : ''}`}>
        <div className="hb-ornament-line" />
        <div className="hb-ornament-diamond" />
        <div className="hb-ornament-line" />
      </div>
    </>
  );

  return (
    <section ref={sectionRef} style={{ width: '100%', position: 'relative' }}>
      <style>{`

        /* ══════════════════════════════════════════════════════════
           SHARED STYLES (ornaments, text, stats)
        ══════════════════════════════════════════════════════════ */

        .hb-ornament {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 32px;
          opacity: 0;
          transition: opacity 0.7s ease 0.2s;
        }
        .hb-ornament.hb--visible { opacity: 1; }
        .hb-ornament-line {
          width: 0;
          height: 1px;
          background: rgba(255,255,255,0.5);
          transition: width 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s;
        }
        .hb-ornament.hb--visible .hb-ornament-line { width: 60px; }
        .hb-ornament-diamond {
          width: 7px;
          height: 7px;
          border: 1px solid rgba(255,255,255,0.7);
          transform: rotate(45deg) scale(0);
          flex-shrink: 0;
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s;
        }
        .hb-ornament.hb--visible .hb-ornament-diamond { transform: rotate(45deg) scale(1); }

        .hb-eyebrow {
          font-family: Poppins-Medium, sans-serif;
          font-size: 11px;
          letter-spacing: 0.35em;
          text-transform: uppercase;
          color: #717fe0;
          margin: 0 0 18px 0;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.7s ease 0.35s, transform 0.7s ease 0.35s;
        }
        .hb-eyebrow.hb--visible { opacity: 1; transform: translateY(0); }

        .hb-heading {
          font-family: PlayfairDisplay-Bold, serif;
          font-size: 52px;
          line-height: 1.12;
          color: #fff;
          margin: 0 0 24px 0;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.5s,
                      transform 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.5s;
        }
        .hb-heading.hb--visible { opacity: 1; transform: translateY(0); }

        .hb-divider {
          width: 0;
          height: 1px;
          background: rgba(255,255,255,0.35);
          margin: 0 0 24px 0;
          transition: width 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.8s;
        }
        .hb-divider.hb--visible { width: 120px; }

        .hb-body {
          font-family: Poppins-Regular, sans-serif;
          font-size: 16px;
          line-height: 1.85;
          color: rgba(255,255,255,0.75);
          margin: 0 0 48px 0;
          max-width: 580px;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.9s ease 0.75s, transform 0.9s ease 0.75s;
        }
        .hb-body.hb--visible { opacity: 1; transform: translateY(0); }

        .hb-stats {
          display: flex;
          align-items: center;
          gap: 0;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.8s ease 1s, transform 0.8s ease 1s;
        }
        .hb-stats.hb--visible { opacity: 1; transform: translateY(0); }
        .hb-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 0 40px;
        }
        .hb-stat-num {
          font-family: PlayfairDisplay-Bold, serif;
          font-size: 32px;
          color: #fff;
          line-height: 1;
        }
        .hb-stat-label {
          font-family: Poppins-Regular, sans-serif;
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }
        .hb-stat-sep {
          width: 1px;
          height: 40px;
          background: rgba(255,255,255,0.2);
          flex-shrink: 0;
        }
        .hb-ornament-bottom { margin-top: 40px; }

        /* ══════════════════════════════════════════════════════════
           DESKTOP: background-image, min-height 520px
        ══════════════════════════════════════════════════════════ */
        .hb-wrapper-desktop {
          position: relative;
          width: 100%;
          min-height: 520px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background-color: #1a1a1a;
          background-image: url('asset/images/slide-11.png');
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
          background-attachment: scroll;
        }

        .hb-overlay-desktop {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(to right,  rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1)  50%, rgba(0,0,0,0.55) 100%),
            linear-gradient(to bottom, rgba(0,0,0,0.3)  0%, transparent       30%, transparent       70%, rgba(0,0,0,0.3) 100%);
          z-index: 1;
        }

        .hb-content-desktop {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 780px;
          margin: 0 auto;
          padding: 100px 40px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* ══════════════════════════════════════════════════════════
           MOBILE: portrait <img>, full height, no cropping
        ══════════════════════════════════════════════════════════ */
        .hb-wrapper-mobile {
          position: relative;
          width: 100%;
          display: block;
          background-color: #1a1a1a;
        }

        /* Portrait image: full width, auto height = whole image always visible */
        .hb-wrapper-mobile .hb-img {
          display: block;
          width: 100%;
          height: auto;
        }

        /* Overlay for text readability */
        .hb-wrapper-mobile .hb-overlay-mobile {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(to right,  rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.45) 100%),
            linear-gradient(to bottom, rgba(0,0,0,0.2)  0%, rgba(0,0,0,0.0)  40%, rgba(0,0,0,0.5)  100%);
          z-index: 1;
        }

        /* Content centered over the portrait image */
        .hb-wrapper-mobile .hb-content-mobile {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px 20px;
          text-align: center;
        }

        /* Mobile typography adjustments */
        @media (max-width: 768px) {
          .hb-heading   { font-size: 28px; }
          .hb-body      { font-size: 13px; margin-bottom: 24px; }
          .hb-stat      { padding: 0 16px; }
          .hb-stat-num  { font-size: 22px; }
          .hb-ornament  { margin-bottom: 16px; }

          /* Show all content immediately on mobile — no animation delays */
          .hb-ornament,
          .hb-eyebrow,
          .hb-heading,
          .hb-body,
          .hb-stats {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
          .hb-ornament-line    { width: 40px !important;                        transition: none !important; }
          .hb-ornament-diamond { transform: rotate(45deg) scale(1) !important;  transition: none !important; }
          .hb-divider          { width: 80px !important;                        transition: none !important; }
        }

        @media (max-width: 480px) {
          .hb-stats    { flex-wrap: wrap; justify-content: center; gap: 20px; }
          .hb-stat-sep { display: none; }
        }
      `}</style>

      {isMobile ? (
        /* ── MOBILE: portrait image, full height, text overlaid ── */
        <div className="hb-wrapper-mobile">
          <img
            src="asset/images/slide-11-potraite.png"
            alt="Kryptonian Labs Heritage"
            className="hb-img"
          />
          <div className="hb-overlay-mobile" />
          <div className="hb-content-mobile">
            {content(true)}
          </div>
        </div>
      ) : (
        /* ── DESKTOP: background-image, min-height, scroll animations ── */
        <div className="hb-wrapper-desktop">
          <div className="hb-overlay-desktop" />
          <div className="hb-content-desktop">
            {content(visible)}
          </div>
        </div>
      )}
    </section>
  );
};

export default HeritageBanner;